import { TrashIcon } from '@heroicons/react/16/solid';
import { FieldArray, useFormikContext } from 'formik';
import { useState } from 'react';
import { Button } from 'react-daisyui';
import { useTranslation } from 'react-i18next';
import {
  INTERACTIVE_LIMITS,
  tInteractiveButton,
  tInteractiveOutbound,
} from '../../../../../../Chat.types';
import { slugifyId } from '../../InteractiveAction.functions';
import UTF16Counter from '../UTF16Counter/UTF16Counter.component';

// Reply buttons: 1–3 rows of { id, title }. Title drives auto-generated id
// (slugified) until the seller opens the "Avanzado" panel and edits the id
// manually — from that point on, title changes no longer overwrite the
// custom id. Auto-generation keeps the critical bot-correlation field
// populated without asking manual sellers to care about it.
//
// Layout: header row (label | Avanzado toggle | 🗑 remove) sits ABOVE the
// title input, so each button takes ~3 lines instead of 5 — critical for the
// 380 px cap on the normal-view composer.
export default function ReplyButtonsBuilder() {
  const { t } = useTranslation(['chat/chat']);
  const { values, handleChange, setFieldValue } =
    useFormikContext<tInteractiveOutbound>();
  const [manualIds, setManualIds] = useState<Set<number>>(new Set());
  const [openAdvanced, setOpenAdvanced] = useState<Set<number>>(new Set());

  if (values.type !== 'button') return null;

  const buttons = values.action.buttons;

  function toggleAdvanced(index: number) {
    setOpenAdvanced((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  }

  return (
    <FieldArray name="action.buttons">
      {({ push, remove }) => (
        <div className="InteractiveAction__Builder">
          <p className="InteractiveAction__Builder__Title">
            {t('interactive.button.title', {
              count: buttons.length,
              max: INTERACTIVE_LIMITS.BUTTON_COUNT_MAX,
            })}
          </p>

          {buttons.map((btn: tInteractiveButton, index: number) => {
            const isAdvancedOpen = openAdvanced.has(index);
            const canRemove =
              buttons.length > INTERACTIVE_LIMITS.BUTTON_COUNT_MIN;

            return (
              <div
                key={index}
                className="InteractiveAction__Builder__Row"
                data-testid={`interactive-button-row-${index}`}
              >
                <div className="InteractiveAction__Builder__RowHeader">
                  <label className="InteractiveAction__Builder__RowHeader__Label">
                    {t('interactive.button.titleFieldLabel', {
                      index: index + 1,
                    })}
                  </label>
                  <button
                    type="button"
                    className="InteractiveAction__Builder__RowHeader__Advanced"
                    aria-expanded={isAdvancedOpen}
                    onClick={() => toggleAdvanced(index)}
                  >
                    {isAdvancedOpen ? '▾ ' : '▸ '}
                    {t('interactive.button.advancedToggle')}
                  </button>
                  {canRemove && (
                    <button
                      type="button"
                      className="InteractiveAction__Builder__RowHeader__Remove"
                      title={t('interactive.button.remove')}
                      aria-label={t('interactive.button.remove')}
                      onClick={() => {
                        remove(index);
                        setManualIds(new Set());
                        setOpenAdvanced(new Set());
                      }}
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  )}
                </div>

                <input
                  type="text"
                  dir="auto"
                  name={`action.buttons[${index}].title`}
                  // Native maxLength stops typing at the Meta limit and
                  // truncates pasted content. Browsers measure in UTF-16
                  // code units (same unit as utf16Count + Meta's server),
                  // so the counter and the hard stop agree exactly.
                  maxLength={INTERACTIVE_LIMITS.BUTTON_TITLE}
                  className="InteractiveAction__Field__Input"
                  placeholder={t('interactive.button.titlePlaceholder')}
                  value={btn.title}
                  onChange={(e) => {
                    const nextTitle = e.target.value;
                    setFieldValue(
                      `action.buttons[${index}].title`,
                      nextTitle,
                    );
                    if (!manualIds.has(index)) {
                      setFieldValue(
                        `action.buttons[${index}].id`,
                        slugifyId(nextTitle),
                      );
                    }
                  }}
                />
                <UTF16Counter
                  value={btn.title}
                  max={INTERACTIVE_LIMITS.BUTTON_TITLE}
                />

                {isAdvancedOpen && (
                  <div className="InteractiveAction__Advanced__Content">
                    <label className="InteractiveAction__Field__Label">
                      {t('interactive.button.idFieldLabel')}
                    </label>
                    <input
                      type="text"
                      name={`action.buttons[${index}].id`}
                      maxLength={INTERACTIVE_LIMITS.BUTTON_ID}
                      className="InteractiveAction__Field__Input"
                      placeholder={t('interactive.button.idPlaceholder')}
                      value={btn.id}
                      onChange={(e) => {
                        handleChange(e);
                        setManualIds((prev) => {
                          const next = new Set(prev);
                          next.add(index);
                          return next;
                        });
                      }}
                    />
                    <p className="InteractiveAction__Advanced__Help">
                      {t('interactive.button.idHelp')}
                    </p>
                  </div>
                )}
              </div>
            );
          })}

          {buttons.length < INTERACTIVE_LIMITS.BUTTON_COUNT_MAX && (
            <Button
              type="button"
              color="ghost"
              size="sm"
              className="InteractiveAction__Builder__AddButton"
              onClick={() => push({ id: '', title: '' })}
            >
              {t('interactive.button.add')}
            </Button>
          )}
        </div>
      )}
    </FieldArray>
  );
}
